import type { HasChildren } from '@meterup/atto';
import type { Transition } from 'motion/react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import {
  backgrounds,
  Button,
  ControlGroup,
  darkThemeSelector,
  selectors,
  shadows,
} from '@meterup/atto';
import { useGesture } from '@use-gesture/react';
import { animate, motion, useMotionValue, useReducedMotion } from 'motion/react';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import useMeasure from 'react-use-measure';

import { colors, styled } from '../stitches';

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
const BUTTON_ZOOM_FACTOR = 1.5;
// Scale change per pixel of wheel travel, so a trackpad and a notched wheel both feel right.
const WHEEL_ZOOM_SENSITIVITY = 0.002;
const REVEAL_MARGIN = 24;
const CAMERA_TRANSITION: Transition = { duration: 0.3, ease: 'easeOut' };

const Container = styled('div', {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  background: backgrounds.dottedLight,
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$10',
  outline: 'none',

  '&:before': {
    content: '',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },

  [darkThemeSelector]: {
    background: backgrounds.dottedDark,
    backgroundColor: colors.bgApplicationDark,
  },

  [selectors.focus]: {
    '&:before': {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },
});
Container.displayName = 'Container';

const Viewport = styled('div', {
  position: 'relative',
  zIndex: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  touchAction: 'none',
  userSelect: 'none',
  cursor: 'grab',

  '&:active': {
    cursor: 'grabbing',
  },
});
Viewport.displayName = 'PanZoomViewport';

const Content = styled(motion.div, {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transformOrigin: '0 0',
});
Content.displayName = 'PanZoomContent';

const RevealBox = styled('div', {
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none',
});
RevealBox.displayName = 'PanZoomRevealBox';

const ControlsRegion = styled(ControlGroup, {
  position: 'absolute',
  bottom: '$16',
  right: '$16',
  zIndex: 2,
  padding: '$8 $10',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$12',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
ControlsRegion.displayName = 'ControlsRegion';

// A smaller bar for charts, where the grouped buttons draw their own edges and shadows.
const CompactControlsRegion = styled(ControlGroup, {
  position: 'absolute',
  right: '$8',
  bottom: '$8',
  zIndex: 2,
});
CompactControlsRegion.displayName = 'CompactControlsRegion';

const clampScale = (scale: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

type Camera = { x: number; y: number; scale: number };

const RevealContext = createContext<((element: Element) => void) | null>(null);

/**
 * An invisible box, positioned by its `style` inside a `PanAndZoomRegion`, that the region brings
 * into view when it mounts, zooming out only as far as it takes to fit. Give it a `key` that changes
 * with what it marks, so each new target is revealed once. Left alone while it's already in view.
 */
export function PanAndZoomReveal({ style }: { style: CSSProperties }) {
  const reveal = useContext(RevealContext);
  const revealOnMount = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) reveal?.(element);
    },
    [reveal],
  );
  return <RevealBox ref={revealOnMount} style={style} />;
}

/**
 * Pans by dragging, and zooms by wheel, trackpad or touch pinch, about the pointer. The camera is a
 * set of Motion values, so moves from the controls and from `PanAndZoomReveal` animate.
 */
export function PanAndZoomRegion({
  children,
  controls = 'default',
}: HasChildren & {
  /** `compact` gives the zoom controls less of the canvas, e.g. for charts in small spaces. */
  controls?: 'compact' | 'default';
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pendingReveal = useRef<Element | null>(null);
  const draggedRef = useRef(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);

  // Deferred to the next frame: re-rendering inside a pointer event rebinds the gestures and drops
  // the drag in progress.
  const [zoom, setZoom] = useState(1);
  const [isMoved, setIsMoved] = useState(false);
  const syncFrame = useRef<number | null>(null);
  const syncControls = useCallback(() => {
    if (syncFrame.current !== null) return;
    syncFrame.current = requestAnimationFrame(() => {
      syncFrame.current = null;
      setZoom(scale.get());
      setIsMoved(scale.get() !== 1 || Math.abs(x.get()) > 2 || Math.abs(y.get()) > 2);
    });
  }, [x, y, scale]);

  const reduceMotion = useReducedMotion();

  const stopCamera = useCallback(() => {
    x.stop();
    y.stop();
    scale.stop();
  }, [x, y, scale]);

  const moveCamera = useCallback(
    (camera: Camera, animated: boolean) => {
      stopCamera();
      if (!animated || reduceMotion) {
        x.set(camera.x);
        y.set(camera.y);
        scale.set(camera.scale);
        syncControls();
        return;
      }
      const transition = { ...CAMERA_TRANSITION, onUpdate: syncControls, onComplete: syncControls };
      animate(x, camera.x, transition);
      animate(y, camera.y, transition);
      animate(scale, camera.scale, transition);
    },
    [stopCamera, syncControls, reduceMotion, x, y, scale],
  );

  /** The camera that scales to `nextScale` while keeping the point at `originX`, `originY` still. */
  const zoomedAbout = useCallback(
    (nextScale: number, originX: number, originY: number): Camera => {
      const ratio = clampScale(nextScale) / scale.get();
      return {
        x: originX - (originX - x.get()) * ratio,
        y: originY - (originY - y.get()) * ratio,
        scale: clampScale(nextScale),
      };
    },
    [x, y, scale],
  );

  const [measureViewport, viewportSize] = useMeasure();

  const zoomFromCenter = (factor: number) => {
    if (viewportSize.width === 0) return;
    moveCamera(
      zoomedAbout(scale.get() * factor, viewportSize.width / 2, viewportSize.height / 2),
      true,
    );
  };

  const revealElement = useCallback(
    (element: Element) => {
      const viewport = viewportRef.current;
      if (!viewport) {
        pendingReveal.current = element;
        return;
      }
      const view = viewport.getBoundingClientRect();
      const box = element.getBoundingClientRect();
      if (
        box.left >= view.left &&
        box.right <= view.right &&
        box.top >= view.top &&
        box.bottom <= view.bottom
      ) {
        return;
      }

      const current = scale.get();
      const width = box.width / current;
      const height = box.height / current;
      const left = (box.left - view.left - x.get()) / current;
      const top = (box.top - view.top - y.get()) / current;
      const nextScale = clampScale(
        Math.min(
          current,
          (view.width - REVEAL_MARGIN * 2) / width,
          (view.height - REVEAL_MARGIN * 2) / height,
        ),
      );
      moveCamera(
        {
          x: view.width / 2 - (left + width / 2) * nextScale,
          y: view.height / 2 - (top + height / 2) * nextScale,
          scale: nextScale,
        },
        true,
      );
    },
    [moveCamera, x, y, scale],
  );

  // A reveal inside can mount before the viewport's ref is set; it's handled once the ref lands.
  const setViewport = useCallback(
    (element: HTMLDivElement | null) => {
      viewportRef.current = element;
      const pending = pendingReveal.current;
      if (element && pending) {
        pendingReveal.current = null;
        revealElement(pending);
      }
    },
    [revealElement],
  );

  const viewportRefs = useMemo(
    () => mergeRefs([setViewport, measureViewport]),
    [setViewport, measureViewport],
  );

  const viewportPoint = (clientX: number, clientY: number) => {
    const view = viewportRef.current?.getBoundingClientRect();
    return view ? [clientX - view.left, clientY - view.top] : [clientX, clientY];
  };

  useGesture(
    {
      onDragStart: () => {
        draggedRef.current = false;
        stopCamera();
      },
      // The click that ends a drag fires before this, so the flag can't outlive it.
      onDragEnd: () => {
        setTimeout(() => {
          draggedRef.current = false;
        }, 0);
      },
      onDrag: ({ offset: [offsetX, offsetY], pinching, cancel, tap }) => {
        if (pinching) {
          cancel();
          return;
        }
        if (!tap) draggedRef.current = true;
        x.set(offsetX);
        y.set(offsetY);
        syncControls();
      },
      onPinchStart: stopCamera,
      onPinch: ({ origin: [clientX, clientY], offset: [nextScale] }) => {
        const [originX, originY] = viewportPoint(clientX, clientY);
        moveCamera(zoomedAbout(nextScale, originX, originY), false);
      },
      onWheel: ({ event, delta: [, deltaY], pinching }) => {
        if (pinching) return;
        event.preventDefault();
        stopCamera();
        const [originX, originY] = viewportPoint(event.clientX, event.clientY);
        const nextScale = scale.get() * Math.exp(-deltaY * WHEEL_ZOOM_SENSITIVITY);
        moveCamera(zoomedAbout(nextScale, originX, originY), false);
      },
    },
    {
      target: viewportRef,
      eventOptions: { passive: false },
      drag: { from: () => [x.get(), y.get()], filterTaps: true, pointer: { buttons: 1 } },
      pinch: { from: () => [scale.get(), 0], scaleBounds: { min: MIN_SCALE, max: MAX_SCALE } },
    },
  );

  // A drag ends on whatever is under the pointer, which shouldn't count as a click on it.
  const swallowClickAfterDrag = (event: ReactMouseEvent) => {
    if (!draggedRef.current) return;
    draggedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const canZoomIn = zoom < MAX_SCALE - 1e-6;
  const canZoomOut = zoom > MIN_SCALE + 1e-6;

  const zoomControls = (
    <>
      <Button
        variant="secondary"
        arrangement="hidden-label"
        icon="plus"
        onClick={() => zoomFromCenter(BUTTON_ZOOM_FACTOR)}
        disabled={!canZoomIn}
      >
        Zoom in
      </Button>
      <Button
        variant="secondary"
        arrangement="hidden-label"
        icon="minus"
        onClick={() => zoomFromCenter(1 / BUTTON_ZOOM_FACTOR)}
        disabled={!canZoomOut}
      >
        Zoom out
      </Button>
      <Button
        variant="secondary"
        arrangement="hidden-label"
        icon="arrows-rotate"
        onClick={() => moveCamera({ x: 0, y: 0, scale: 1 }, true)}
        disabled={!isMoved}
      >
        Reset
      </Button>
    </>
  );

  return (
    <Container tabIndex={-1}>
      <Viewport
        ref={viewportRefs}
        onClickCapture={swallowClickAfterDrag}
        onDragStart={(event) => event.preventDefault()}
      >
        <RevealContext.Provider value={revealElement}>
          <Content style={{ x, y, scale }}>{children}</Content>
        </RevealContext.Provider>
      </Viewport>
      {controls === 'compact' ? (
        <CompactControlsRegion size="x-small">{zoomControls}</CompactControlsRegion>
      ) : (
        <ControlsRegion size="medium">{zoomControls}</ControlsRegion>
      )}
    </Container>
  );
}
